/**
 * Bridges MCP servers into RuntimeToolDef records that FullAgenticChat
 * can consume as client-side (runTime) tools.
 *
 * For each configured MCP server:
 *  1. Establishes an MCP session (initialize handshake)
 *  2. Discovers tools via tools/list
 *  3. Wraps each tool as a RuntimeToolDef whose callback POSTs tools/call
 */

import { McpClient, type TokenProvider } from "./mcpClient";
import type { McpServerConfig } from "../config/mcpServers";
import {
  toToolDisplayName,
  type RuntimeToolDef,
} from "../tools/runtimeTypes";

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Sanitise a server name into a safe tool-name prefix.
 * "file-flipper" → "file_flipper"
 */
function sanitisePrefix(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, "_").replace(/_+/g, "_");
}

/**
 * Initialise all configured MCP servers, discover their tools,
 * and return a flat Record<toolName, RuntimeToolDef>.
 *
 * Tool names are prefixed with the server name to avoid collisions:
 *   e.g. `file_flipper__reverse_file`
 */
export async function discoverMcpTools(
  servers: McpServerConfig[],
  getAccessToken?: TokenProvider,
): Promise<Record<string, RuntimeToolDef>> {
  const allTools: Record<string, RuntimeToolDef> = {};

  await Promise.all(
    servers.map(async (server) => {
      const client = new McpClient(server.url, getAccessToken);

      try {
        await client.initialize();

        const tools = await client.listTools();

        const prefix = sanitisePrefix(server.name);

        for (const tool of tools) {
          const prefixedName = `${prefix}__${tool.name}`;

          allTools[prefixedName] = {
            definition: {
              name: prefixedName,
              description: `[${server.name}] ${tool.description}`,
              deferred: true,
              title: toToolDisplayName(prefixedName),
              parameters: {
                type: "object",
                properties: tool.inputSchema?.properties ?? {},
                required: tool.inputSchema?.required,
              },
            },
            callback: async (args: Record<string, unknown>) => {
              try {
                const result = await client.callTool(tool.name, args);
                return JSON.stringify({ success: true, data: result });
              } catch (err) {
                const msg = err instanceof Error ? err.message : String(err);
                return JSON.stringify({ success: false, error: msg });
              }
            },
            timeOutInMs: DEFAULT_TIMEOUT_MS,
          };
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(`[MCP] Failed to connect to "${server.name}":`, msg);
      }
    }),
  );

  return allTools;
}
